import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MdArrowBack,
  MdExpandMore,
  MdCalendarToday,
  MdLocationOn,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { fetchUpcomingEventsRequest } from "../../redux/slices/upcomingEventsSlice";
import {
  createCampaignRequest,
  resetCampaignCreation,
} from "../../redux/slices/campaignCreationSlice";
import { fetchCampaignsRequest } from "../../redux/slices/campaignsSlice";
import { monitorCampaignGeneration } from "../../services/websocket";
import ENDPOINTS from "../../api/endPoints";
import { getCampaign as fetchCampaignById } from "../../api/contentApi";

import type { RootState } from "../../redux/store";
import type { CombinedEvent } from "../upcomingEvents/types";

import CreateOwnForm from "../editCampaign/CreateOwnForm";
import { useRestaurantNav } from "../../hooks/useRestaurantNav";
import { MONTHS, SAMPLE_AD_CARDS } from "../../constants";

const getCurrentMonth = () => MONTHS[new Date().getMonth()];

const postYourOwnCategory = "postyourown";

const normalizeCategory = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const CampaignCreation: React.FC = () => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();
  const location = useLocation();
  const restaurantId = useSelector(
    (state: RootState) => state.clover.restaurantId,
  );

  // Get campaign creation state
  const {
    loading: creationLoading,
    success: creationSuccess,
    error: creationError,
    response: creationResponse,
  } = useSelector((state: RootState) => state.campaignCreation);

  // Get upcoming events from Redux store
  const { combinedEvents, loading: eventsLoading } = useSelector(
    (state: RootState) => state.upcomingEvents,
  );

  // Edit mode - when navigated here from Playground's "Change Image" / "Edit Content" / "Regenerate"
  const editState = (location.state as any) || {};
  const editMode: boolean = !!editState.editMode;
  const updateMode: "text_only" | "full_update" | "image_only" =
    editState.updateMode || "full_update";
  const editCampaignId: string | undefined = editState.campaignId;
  const [postYourOwn, setPostYourOwn] = useState(false);
  const editCampaignData: any = editState.campaignData;

  const [currentCampaignData, setCurrentCampaignData] =
    useState<any>(editCampaignData);

  const [selectedMonth, setSelectedMonth] =
    useState<(typeof MONTHS)[number]>(getCurrentMonth());

  const [selectedEvent, setSelectedEvent] = useState<CombinedEvent | null>(
    null,
  );
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Live generation state (shown in-page instead of navigating to processing page)
  const [isGenerating, setIsGenerating] = useState(false);
  const [genRequestId, setGenRequestId] = useState<string | null>(null);
  const [genCampaignId, setGenCampaignId] = useState<string | null>(null);
  const [genProgress, setGenProgress] = useState(0);
  const [genMessages, setGenMessages] = useState<string[]>([]);
  const [shuffleIndex, setShuffleIndex] = useState(0);
  const [refImages, setRefImages] = useState<string[]>([]);
  const hasNavigatedRef = useRef(false);

  const monthDropdownRef = useRef<HTMLDivElement>(null);
  const eventDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentCampaignData(editCampaignData);
  }, [editCampaignId]);

  useEffect(() => {
    if (!editMode || !currentCampaignData) return;

    const campaign =
      currentCampaignData.campaignData ||
      currentCampaignData.campaign ||
      currentCampaignData ||
      {};
    const category = campaign.category || currentCampaignData.category || "";

    setPostYourOwn(normalizeCategory(category) === postYourOwnCategory);
  }, [editMode, currentCampaignData]);

  const editFlyerImageUrl = useMemo(() => {
    if (!currentCampaignData) return "";
    return (
      currentCampaignData.flyer?.image?.image?.storage_url ||
      currentCampaignData.flyer?.image?.storage_url ||
      ""
    );
  }, [currentCampaignData]);

  const editFormInitialData = useMemo(() => {
    if (!currentCampaignData) return undefined;
    const campaign =
      currentCampaignData.campaignData ||
      currentCampaignData.campaign ||
      currentCampaignData ||
      {};
    const flyerImage =
      campaign.flyerImage ||
      currentCampaignData.flyerImage ||
      currentCampaignData.flyer?.image?.image?.storage_url ||
      currentCampaignData.flyer?.image?.storage_url ||
      "";

    return {
      title: currentCampaignData.title || campaign.title || "",
      offerDetails: campaign.offerDetails || "",
      Instructions: campaign.instructions || "",
      tagLine: campaign.tagLine || "",
      validFrom: campaign.validFrom
        ? String(campaign.validFrom).split("T")[0]
        : "",
      validTo: campaign.validTo ? String(campaign.validTo).split("T")[0] : "",
      contactInfo: campaign.contactInfo || "",
      refImage: campaign.refImage || [],
      category: campaign.category || currentCampaignData.category || "",
      flyerImage,
      themeColour: campaign.themeColour || "#EE4423",
      menuItemNames: (campaign.menuItems || [])
        .map((m: any) => m.items)
        .filter(Boolean),
    };
  }, [currentCampaignData]);

  // Fetch upcoming events on mount (only needed for the create flow)
  useEffect(() => {
    if (
      !editMode &&
      restaurantId &&
      combinedEvents.length === 0 &&
      !eventsLoading
    ) {
      dispatch(fetchUpcomingEventsRequest({ restaurantId, countryCode: "US" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, dispatch, editMode]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        monthDropdownRef.current &&
        !monthDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMonthDropdownOpen(false);
      }
      if (
        eventDropdownRef.current &&
        !eventDropdownRef.current.contains(event.target as Node)
      ) {
        setIsEventDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter events by selected month (includes past events, which are shown but disabled), sorted by date
  const filteredEvents = useMemo(() => {
    const monthNum = (MONTHS.indexOf(selectedMonth) + 1)
      .toString()
      .padStart(2, "0");

    return combinedEvents
      .filter((event) => event.date.includes(`-${monthNum}-`))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [combinedEvents, selectedMonth]);

  const isEventInPast = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
  };

  const formatEventDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Cycle the shuffling cards continuously while generating (only for create flow without a real flyer image)
  useEffect(() => {
    if (!isGenerating || editMode) return;
    const interval = setInterval(() => {
      setShuffleIndex((prev) => (prev + 1) % SAMPLE_AD_CARDS.length);
    }, 1800);
    return () => clearInterval(interval);
  }, [isGenerating, editMode]);

  // Handle campaign creation success - kick off live in-page generation view
  useEffect(() => {
    if (editMode) return;
    if (creationSuccess && !creationLoading && restaurantId) {
      const requestId =
        creationResponse?.requestId || creationResponse?.data?.requestId;
      const campaignId =
        creationResponse?.data?.id ||
        creationResponse?.id ||
        creationResponse?.campaignId ||
        creationResponse?.data?.campaignId;

      if (campaignId) {
        setGenCampaignId(campaignId);
        setGenRequestId(requestId || null);
        setGenProgress(5);
        setGenMessages(["Starting campaign generation..."]);
        hasNavigatedRef.current = false;
        setIsGenerating(true);
      } else {
        navigate("/dashboard");
      }

      dispatch(resetCampaignCreation());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creationSuccess, creationLoading, restaurantId, editMode]);

  const handleUpdateCompleted = async () => {
    if (hasNavigatedRef.current || !genCampaignId || !restaurantId) return;
    hasNavigatedRef.current = true;

    // Refresh the campaigns list in the background (used elsewhere in the app)
    dispatch(fetchCampaignsRequest());

    try {
      const data = await fetchCampaignById(genCampaignId, restaurantId);
      if (data?.success && data.data) {
        setCurrentCampaignData(data.data);
      }
    } catch (error) {
      console.error(
        "[Campaign Creation] Error refreshing updated campaign:",
        error,
      );
    } finally {
      setIsGenerating(false);
      setUpdateSubmitting(false);
      setGenProgress(0);
      setGenMessages([]);
      setGenRequestId(null);
    }
  };

  // Monitor live progress via SSE and update in-page UI
  useEffect(() => {
    if (!isGenerating || !restaurantId) return;
    const monitorId = genRequestId || genCampaignId;
    if (!monitorId) return;

    const goToFlyer = () => {
      if (hasNavigatedRef.current || !genCampaignId) return;
      hasNavigatedRef.current = true;
      // Refresh the campaigns list once generation has fully completed
      dispatch(fetchCampaignsRequest());
      setTimeout(() => {
        navigate(`/flyer?campaignId=${genCampaignId}&campaignType=manual`, {
          state: { campaignId: genCampaignId, campaignType: "manual" },
        });
      }, 700);
    };

    const handleDone = () => {
      if (editMode) {
        handleUpdateCompleted();
      } else {
        goToFlyer();
      }
    };

    const cleanup = monitorCampaignGeneration(
      restaurantId,
      monitorId,
      undefined,
      (event) => {
        if (event.type === "progress" && event.data) {
          const pct = event.data.percentCompleted;
          if (typeof pct === "number") {
            setGenProgress(pct);
          }
          if (event.data.message) {
            setGenMessages((prev) =>
              prev[prev.length - 1] === event.data!.message
                ? prev
                : [...prev, event.data!.message],
            );
          }
          const anyData = event.data as any;
          if (anyData.status === "completed" || pct >= 100) {
            setGenProgress(100);
            handleDone();
          }
        } else if (event.type === "done") {
          setGenProgress(100);
          handleDone();
        }
      },
      (error) => {
        console.error("[Campaign Creation] Monitor error:", error);
      },
    );

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenerating, genRequestId, genCampaignId, restaurantId]);

  const handleCreateOwn = async (formData: any) => {
    if (!restaurantId) return;

    // ----- Edit / Update flow -----
    if (editMode && editCampaignId) {
      try {
        setUpdateError(null);
        setUpdateSubmitting(true);

        const campaign =
          editCampaignData?.campaignData || editCampaignData?.campaign || {};
        const campaignType =
          editCampaignData?.type || campaign.type || "manual";
        const campaignEventId =
          editCampaignData?.eventId || campaign.eventId || "";
        const campaignEvent = editCampaignData?.event || campaign.event || {};
        const campaignFlyer = editCampaignData?.flyer || campaign.flyer || {};

        const commonUpdatePayload = {
          id: editCampaignId,
          mode: updateMode,
          type: campaignType,
          title: formData.title,
          description: formData.title,
          offerDetails: formData.offerInfo || "",
          tagLine: formData.tagLine || "",
          contactInfo: formData.contactPhone || "",
          validFrom: formData.validFrom,
          validTo: formData.validTo,
          themeColour: formData.themeColors?.[0] || "#EE4423",
          menuItems: formData.menuItems || [],
          combo: formData.combo || [],
          eventId: campaignEventId,
          event: campaignEvent,
          flyer: campaignFlyer,
        };

        if (postYourOwn && !formData.flyerImage) {
          throw new Error("Please upload the flyer image before updating.");
        }

        const updatePayload = postYourOwn
          ? {
              ...commonUpdatePayload,
              category: postYourOwnCategory,
              flyerImage: formData.flyerImage,
            }
          : {
              ...commonUpdatePayload,
              instructions: formData.Instructions || "",
              refImage: formData.refImage || [],
            };

        setRefImages(postYourOwn ? [] : formData.refImage || []);

        const response = await axios.put(
          ENDPOINTS.updateCampaign(editCampaignId),
          updatePayload,
          {
            headers: {
              "Content-Type": "application/json",
              "restaurant-id": restaurantId,
              "account-id": "",
            },
          },
        );

        const requestId =
          response.data?.data?.requestId ||
          response.data?.requestId ||
          response.data?.data?.request_id ||
          response.data?.request_id;
        const updatedCampaignId =
          response.data?.data?.id || response.data?.id || editCampaignId;

        if (!requestId) {
          // No SSE tracking available - go straight to the flyer page
          navigate(
            `/flyer?campaignId=${updatedCampaignId}&campaignType=manual`,
            {
              state: { campaignId: updatedCampaignId, campaignType: "manual" },
            },
          );
          return;
        }

        setGenCampaignId(updatedCampaignId);
        setGenRequestId(requestId);
        setGenProgress(10);
        setGenMessages(["Updating your campaign..."]);
        hasNavigatedRef.current = false;
        setIsGenerating(true);
      } catch (error: any) {
        console.error("Error updating campaign:", error);
        setUpdateError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to update campaign",
        );
        setUpdateSubmitting(false);
      }
      return;
    }

    // ----- Create new campaign flow -----
    try {
      if (postYourOwn && !formData.flyerImage) {
        throw new Error("Please upload the flyer image before creating.");
      }

      const commonPayload = {
        restaurantId,
        type: "manual",
        title: formData.title || "Untitled Campaign",
        eventId: selectedEvent?.id || "",
        event: selectedEvent || {},
        offerDetails: formData.offerInfo || "",
        description: formData.description || "",
        tagLine: formData.tagLine || "",
        contactInfo: formData.contactPhone || "",
        themeColour: formData.themeColors?.[0] || "",
        validFrom: formData.validFrom,
        validTo: formData.validTo,
        menuItems: formData.menuItems || [],
        combo: formData.combo || [],
      };

      const payload = postYourOwn
        ? {
            ...commonPayload,
            category: postYourOwnCategory,
            flyerImage: formData.flyerImage,
          }
        : {
            ...commonPayload,
            instructions: formData.Instructions || "",
            refImage: formData.refImage || [],
          };

      setRefImages(postYourOwn ? [] : formData.refImage || []);
      dispatch(createCampaignRequest(payload));
    } catch (error) {
      console.error("Error creating campaign:", error);
    }
  };

  const latestMessage =
    genMessages[genMessages.length - 1] ||
    "Initializing campaign generation...";
  const isSubmittingForm = editMode ? updateSubmitting : creationLoading;

  return (
    <div className="h-screen overflow-hidden bg-[#0a0a0a] flex">
      {/* Left Side Panel - expands to full width while generating */}
      <motion.div
        animate={{ width: isGenerating ? "100%" : "30%" }}
        transition={{ duration: 0.6, ease: "easeInOut" }}
        className="bg-[#2a2a2a] h-screen overflow-hidden p-8 flex flex-col relative backdrop-blur-xl border border-gray-700/50 shadow-2xl flex-shrink-0"
      >
        {/* Back Button - Top Left */}
        <button
          onClick={() => {
            if (editMode && editCampaignId) {
              navigate(
                `/flyer?campaignId=${editCampaignId}&campaignType=manual`,
                {
                  state: { campaignId: editCampaignId, campaignType: "manual" },
                },
              );
            } else {
              navigate("/dashboard");
            }
          }}
          className="absolute top-8 left-8 p-3 rounded-sm cursor-pointer  hover:bg-white/10 transition-colors z-10"
        >
          <MdArrowBack className="text-white text-xl" />
        </button>

        <AnimatePresence mode="wait">
          {!isGenerating ? (
            <motion.div
              key="intro"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              {/* Title Section - Centered */}
              <div className="text-center mb-10">
                <h1 className="text-3xl font-medium text-brand-primary mb-4">
                  {editMode ? "Update Campaign" : "Create Campaign"}
                </h1>
                <p className="text-white text-sm">
                  Build personalized campaigns in just minutes
                </p>
              </div>

              {editMode && editFlyerImageUrl ? (
                /* Edit Mode: Show the actual flyer being edited */
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="w-full max-w-xs"
                >
                  <div className="relative rounded-xl overflow-hidden shadow-3xl">
                    <img
                      src={editFlyerImageUrl}
                      alt={editFormInitialData?.title || "Campaign Flyer"}
                      className="w-full h-[50vh] object-contain"
                    />
                  </div>
                </motion.div>
              ) : (
                /* Sample Campaign Cards - Fanned/Overlapping Layout (vertical flyer-shaped cards, no images) */
                <div className="relative w-full max-w-md h-80">
                  {SAMPLE_AD_CARDS.map((card, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{
                        opacity: 1,
                        scale: 0.85,
                        rotate: (index - 1) * 15,
                        x: (index - 1) * 30,
                        y: index * 8,
                      }}
                      transition={{ delay: index * 0.15, duration: 0.5 }}
                      className="absolute inset-0 flex items-center justify-center"
                      style={{
                        zIndex: index === 1 ? 3 : index === 0 ? 1 : 2,
                      }}
                    >
                      <div className="w-44 h-64 rounded-xl shadow-2xl border-2 border-white/10 bg-[#1f1f1f] flex flex-col justify-between p-4 overflow-hidden">
                        <div
                          className="w-10 h-1.5 rounded-full"
                          style={{ backgroundColor: card.accent }}
                        />
                        <div>
                          <p className="text-white text-sm font-semibold leading-tight">
                            {card.title}
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            {card.subtitle}
                          </p>
                        </div>
                        <div className="flex gap-1.5">
                          <div className="h-1.5 w-8 rounded-full bg-white/10" />
                          <div className="h-1.5 w-5 rounded-full bg-white/10" />
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="generating"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="flex-1 flex flex-col items-center justify-center gap-12"
            >
              {editMode && editFlyerImageUrl ? (
                <div className="w-full max-w-xs">
                  <div className="relative rounded-xl overflow-hidden shadow-2xl border border-white/10">
                    <img
                      src={editFlyerImageUrl}
                      alt={editFormInitialData?.title || "Campaign Flyer"}
                      className="w-full h-auto object-contain"
                    />

                    {/* Dimmed overlay */}
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
                      <div className="w-full max-w-xs text-center space-y-4">
                        <div className="h-2.5 w-full bg-gray-700/40 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-brand-primary to-orange-600 rounded-full"
                            animate={{ width: `${Math.max(5, genProgress)}%` }}
                            transition={{ duration: 0.5, ease: "easeOut" }}
                          />
                        </div>
                        <div className="text-gray-300 text-xs">
                          {Math.round(genProgress)}%
                        </div>

                        <AnimatePresence mode="wait">
                          <motion.p
                            key={latestMessage}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                            className="text-brand-primary text-sm font-medium min-h-[2.5rem] flex items-center justify-center"
                          >
                            {latestMessage}
                          </motion.p>
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Infinite Shuffling Cards (vertical flyer-shaped cards; shows uploaded reference images if available) */}
                  <div className="relative w-full max-w-md h-80 flex items-center justify-center">
                    {SAMPLE_AD_CARDS.map((card, idx) => {
                      const relIndex =
                        (idx - shuffleIndex + SAMPLE_AD_CARDS.length) %
                        SAMPLE_AD_CARDS.length; // 0 = front card
                      return (
                        <motion.div
                          key={card.title}
                          animate={{
                            scale: relIndex === 0 ? 1 : 0.85 - relIndex * 0.06,
                            rotate: (relIndex - 1) * 14,
                            x: (relIndex - 1) * 34,
                            y: relIndex * 12,
                            opacity: 1,
                            zIndex: SAMPLE_AD_CARDS.length - relIndex,
                          }}
                          transition={{ duration: 1, ease: "easeInOut" }}
                          className="absolute w-40 h-60 rounded-xl shadow-2xl border-2 border-white/10 bg-[#1f1f1f] overflow-hidden"
                        >
                          <div className="w-full h-full flex flex-col justify-between p-4">
                            <div
                              className="w-10 h-1.5 rounded-full"
                              style={{ backgroundColor: card.accent }}
                            />
                            <div>
                              <p className="text-white text-sm font-semibold leading-tight">
                                {card.title}
                              </p>
                              <p className="text-gray-400 text-xs mt-1">
                                {card.subtitle}
                              </p>
                            </div>
                            <div className="flex gap-1.5">
                              <div className="h-1.5 w-8 rounded-full bg-white/10" />
                              <div className="h-1.5 w-5 rounded-full bg-white/10" />
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Progress + Live Streaming Messages (below the shuffling cards for create flow) */}
                  <div className="w-full max-w-sm px-4 text-center space-y-4">
                    <div className="h-2.5 w-full bg-gray-700/40 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-brand-primary to-orange-600 rounded-full"
                        animate={{ width: `${Math.max(5, genProgress)}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      />
                    </div>
                    <div className="text-gray-400 text-xs">
                      {Math.round(genProgress)}%
                    </div>

                    <AnimatePresence mode="wait">
                      <motion.p
                        key={latestMessage}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        className="text-brand-primary text-sm font-medium min-h-[2.5rem] flex items-center justify-center"
                      >
                        {latestMessage}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {!isGenerating && (
          <motion.div
            key="form-panel"
            initial={{ opacity: 1, x: 0 }}
            animate={{ opacity: 1, x: 0, width: "70%" }}
            exit={{ opacity: 0, x: 200, width: "0%" }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="bg-[#1a1a1a] overflow-y-auto flex items-center"
          >
            <div className="w-full p-8">
              {/* Know what's coming next - Event Dropdown with Monthly Filter (only for new campaigns) */}
              {!editMode && (
                <div className="mb-3">
                  <label className="block text-white text-sm font-medium mb-3">
                    Your Next Opportunity
                  </label>
                  <div className="flex gap-3">
                    {/* Month Filter Dropdown */}
                    <div
                      ref={monthDropdownRef}
                      className="relative w-40 flex-shrink-0"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setIsMonthDropdownOpen(!isMonthDropdownOpen)
                        }
                        className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white text-sm focus:outline-none hover:border-gray-600 transition-colors flex items-center justify-between cursor-pointer"
                      >
                        <span>{selectedMonth}</span>
                        <motion.div
                          animate={{ rotate: isMonthDropdownOpen ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <MdExpandMore className="text-xl" />
                        </motion.div>
                      </button>

                      <AnimatePresence>
                        {isMonthDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 right-0 mt-2 bg-[#2a2a2a] border border-gray-700 rounded-xl overflow-hidden z-20 max-h-60 overflow-y-auto"
                          >
                            {MONTHS.map((month) => (
                              <button
                                key={month}
                                type="button"
                                onClick={() => {
                                  setSelectedMonth(month);
                                  setSelectedEvent(null);
                                  setIsMonthDropdownOpen(false);
                                }}

                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-700/50 transition-colors cursor-pointer text-white`}
                                style={
                                  selectedMonth === month
                                    ? { backgroundColor: "#212121" }
                                    : undefined
                                }
                              >
                                {month}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Events Dropdown */}
                    <div ref={eventDropdownRef} className="relative flex-1">
                      <button
                        type="button"
                        onClick={() =>
                          setIsEventDropdownOpen(!isEventDropdownOpen)
                        }
                        className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white text-sm focus:outline-none hover:border-gray-600 transition-colors flex items-center justify-between cursor-pointer"
                      >
                        <span
                          className={
                            selectedEvent ? "text-white" : "text-gray-500"
                          }
                        >
                          {selectedEvent
                            ? selectedEvent.name
                            : "Select an upcoming event (optional)"}
                        </span>
                        <motion.div
                          animate={{ rotate: isEventDropdownOpen ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <MdExpandMore className="text-xl" />
                        </motion.div>
                      </button>

                      <AnimatePresence>
                        {isEventDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 right-0 mt-2 bg-[#2a2a2a] border border-gray-700 rounded-xl overflow-hidden z-20 max-h-80 overflow-y-auto"
                          >
                            {eventsLoading ? (
                              <div className="px-4 py-3 text-sm text-gray-400">
                                Loading events...
                              </div>
                            ) : filteredEvents.length === 0 ? (
                              <div className="px-4 py-3 text-sm text-gray-400">
                                No events found for {selectedMonth}
                              </div>
                            ) : (
                              filteredEvents.map((event) => {
                                const eventId =
                                  event.id || `${event.name}-${event.date}`;
                                const isSelected =
                                  selectedEvent &&
                                  (selectedEvent.id ||
                                    `${selectedEvent.name}-${selectedEvent.date}`) ===
                                    eventId;
                                const isPast = isEventInPast(event.date);
                                const place = [
                                  event.venue,
                                  event.location,
                                  event.state,
                                ]
                                  .filter(Boolean)
                                  .join(", ");
                                return (
                                  <button
                                    key={eventId}
                                    type="button"
                                    disabled={isPast}
                                    onClick={() => {
                                      if (isPast) return;
                                      setSelectedEvent(event);
                                      setIsEventDropdownOpen(false);
                                    }}
                                    className={`w-full px-4 py-3 text-left text-sm transition-colors border-b border-gray-800 last:border-b-0 ${
                                      isPast
                                        ? "cursor-not-allowed opacity-50 text-gray-400"
                                        : `hover:bg-gray-700/50 cursor-pointer ${isSelected ? "text-white" : "text-gray-300"}`
                                    }`}
                                    style={
                                      isSelected && !isPast
                                        ? { backgroundColor: "#212121" }
                                        : undefined
                                    }
                                  >
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                      <span className="font-medium truncate">
                                        {event.name}
                                      </span>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        {isPast && (
                                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-700 text-gray-300">
                                            Past
                                          </span>
                                        )}
                                        {event.type && (
                                          <span
                                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                              isSelected && !isPast
                                                ? "bg-white/20 text-white"
                                                : "text-gray-300"
                                            }`}
                                          >
                                            {event.type}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div
                                      className={`flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] ${isSelected && !isPast ? "text-white/80" : "text-gray-400"}`}
                                    >
                                      <span className="flex items-center gap-1">
                                        <MdCalendarToday className="text-xs" />
                                        {formatEventDate(event.date)}
                                      </span>
                                      {place && (
                                        <span className="flex items-center gap-1 truncate">
                                          <MdLocationOn className="text-xs" />
                                          {place}
                                        </span>
                                      )}
                                    </div>
                                    {(event.marketing_angle ||
                                      event.consumer_behavior) && (
                                      <p
                                        className={`mt-1 text-[11px] line-clamp-2 ${isSelected && !isPast ? "text-white/70" : "text-gray-500"}`}
                                      >
                                        {event.marketing_angle ||
                                          event.consumer_behavior}
                                      </p>
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              )}

            {(!editMode || postYourOwn) && (
              <label className="flex items-center gap-2 mb-2 text-white text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={postYourOwn}
                  onChange={(e) => setPostYourOwn(e.target.checked)}
                  className="w-4 h-4 accent-[#EE4423] cursor-pointer"
                />
                <span>Post your own</span>
              </label>
            )}
              {/* Content */}

              <motion.div
                key={
                  editMode
                    ? `edit-${editCampaignId}`
                    : selectedEvent
                      ? selectedEvent.id ||
                        `${selectedEvent.name}-${selectedEvent.date}`
                      : "default"
                }
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <CreateOwnForm
                  onSubmit={handleCreateOwn}
                  isSubmitting={isSubmittingForm}
                  submitLabel={editMode ? "Update Campaign" : "Create Campaign"}
                  postYourOwn={postYourOwn}
                  initialData={
                    editMode
                      ? editFormInitialData
                      : selectedEvent
                        ? {
                            eventDate: selectedEvent.date,
                            title: selectedEvent.name,
                            promotionType: selectedEvent.type || "",
                            offerDetails: selectedEvent.marketing_angle || "",
                            Instructions: selectedEvent.consumer_behavior || "",
                            tagLine: selectedEvent.marketing_angle || "",
                          }
                        : undefined
                  }
                />
              </motion.div>

              {/* Error Display */}
              {(creationError || updateError) && (
                <div className="mt-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg">
                  <p className="text-red-400 text-sm">
                    {creationError || updateError}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CampaignCreation;
